const cn = (...parts) => parts.filter(Boolean).join(' ');

/**
 * Shape/layout variants. `default` is the original rail with an elevated active chip.
 * `buttonGroup` is the newer Figma treatment: continuous rail with an inset active segment.
 *
 * @type {Record<string, { rail: string; item: string }>}
 */
const VARIANT_STYLES = {
  default: {
    rail: 'flex w-full rounded-lg border border-edge bg-surface-muted p-0.5',
    item:
      'flex-1 rounded-md px-4 py-2 font-medium text-center transition-colors duration-200 active:scale-[0.98]',
  },
  buttonGroup: {
    rail:
      'flex w-full items-center rounded-lg bg-white p-2 shadow-sm dark:bg-[#252A3D]',
    item:
      'flex h-6 min-h-6 max-h-6 flex-1 items-center justify-center rounded px-4 py-0 text-center font-medium leading-6 transition-colors duration-200 active:scale-[0.98]',
  },
};

/**
 * Active segment styles: `default` matches the original elevated chip; `primary` and
 * `classicPrimary` align with {@link Button} `variant="primary"` and `variant="classicPrimary"`.
 *
 * @type {Record<string, Record<string, { active: string; inactive: string }>>}
 */
const ACTIVE_COLOR_STYLES = {
  default: {
    default: {
      active: 'bg-surface border border-edge text-content shadow-sm',
      inactive: 'text-content-muted hover:text-content-secondary',
    },
    primary: {
      active:
        'border-none bg-[linear-gradient(135deg,#7c5cfc,#5b8def)] text-white shadow-none hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7c5cfc]/50 focus-visible:ring-offset-2',
      inactive:
        'border border-transparent text-content-muted hover:bg-white/5 hover:text-content-secondary dark:hover:bg-white/10',
    },
    classicPrimary: {
      active:
        'border border-transparent bg-[#2173FF] text-white shadow-none hover:bg-[#002F80] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#80AFFF] focus-visible:ring-offset-2 dark:bg-[#2173FF] dark:hover:bg-[#04245C]',
      inactive:
        'border border-transparent text-content-muted hover:bg-white/5 hover:text-content-secondary dark:hover:bg-white/10',
    },
  },
  buttonGroup: {
    default: {
      active:
        'bg-[#F3F7FF] text-[#001F3F] shadow-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#80AFFF] focus-visible:ring-offset-2 dark:bg-[#263450] dark:text-[#F8FAFC]',
      inactive:
        'bg-transparent text-[#001F3F] hover:bg-[#F3F7FF]/70 dark:text-[#F8FAFC] dark:hover:bg-[#263450]/70',
    },
    primary: {
      active:
        'bg-[linear-gradient(135deg,#7c5cfc,#5b8def)] text-white shadow-none hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7c5cfc]/50 focus-visible:ring-offset-2',
      inactive:
        'bg-transparent text-[#001F3F] hover:bg-[#F3F7FF]/70 dark:text-[#F8FAFC] dark:hover:bg-[#263450]/70',
    },
    classicPrimary: {
      active:
        'bg-[#2173FF] text-white shadow-none hover:bg-[#002F80] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#80AFFF] focus-visible:ring-offset-2 dark:bg-[#2173FF] dark:hover:bg-[#04245C]',
      inactive:
        'bg-transparent text-[#001F3F] hover:bg-[#F3F7FF]/70 dark:text-[#F8FAFC] dark:hover:bg-[#263450]/70',
    },
  },
};

const getVariantStyles = (variant) =>
  VARIANT_STYLES[variant] ?? VARIANT_STYLES.default;

const getActiveColorStyles = (variant, activeColor) => {
  const variantPalette = ACTIVE_COLOR_STYLES[variant] ?? ACTIVE_COLOR_STYLES.default;
  return variantPalette[activeColor] ?? variantPalette.default;
};

/**
 * Segmented control: mutually exclusive options in a horizontal rail (`role="tablist"`).
 *
 * @param {object} props
 * @param {string} props.value - Selected `option.value`.
 * @param {(next: string) => void} [props.onChange]
 * @param {{ value: string; label: import('react').ReactNode; ariaControls?: string; disabled?: boolean; title?: string }[]} [props.options=[]] - Segment list. `title` is the tooltip, set on a wrapper so hover still shows it on a disabled segment.
 * @param {'default' | 'buttonGroup'} [props.variant='default'] - Visual treatment. `default` preserves the original rail/chip look.
 * @param {'default' | 'primary' | 'classicPrimary'} [props.activeColor='default'] - Active segment look: `default` (surface chip), `primary` (gradient), or `classicPrimary` (legacy blue).
 * @param {string} [props.className] - Rail container (outer `div`).
 * @param {string} [props.itemClassName] - Applied to **every** segment button (active and inactive); use for spacing tweaks, not to replace active colors (use `activeColor` instead).
 * @param {string} [props.textClassName='text-xs'] - Tailwind text-size utilities for segment labels (e.g. `text-sm`).
 * @param {string} [props.ariaLabel] - `aria-label` on the tablist.
 * @param {string} [props.ariaDescribedBy] - id of an element that explains the control (e.g. why an option is disabled); a tooltip is not reachable by keyboard or touch.
 */
const SegmentedControl = ({
  value,
  onChange,
  options = [],
  variant = 'default',
  activeColor = 'default',
  className = '',
  itemClassName = '',
  textClassName = 'text-xs',
  ariaLabel,
  ariaDescribedBy,
}) => {
  const variantStyles = getVariantStyles(variant);
  const palette = getActiveColorStyles(variant, activeColor);

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      className={cn(variantStyles.rail, className)}
    >
      {options.map((option) => {
        const isActive = value === option.value;
        const segmentTone = isActive ? palette.active : palette.inactive;
        // An option with a tooltip sits in a wrapper that owns the `title`, so hover still works while
        // the (disabled) button ignores the pointer. The wrapper stays whether or not the option is
        // disabled, so toggling `disabled` does not remount the button.
        const hasWrapper = Boolean(option.title);
        const button = (
          <button
            key={hasWrapper ? undefined : option.value}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-controls={option.ariaControls}
            disabled={option.disabled}
            onClick={() => onChange?.(option.value)}
            className={cn(
              variantStyles.item,
              textClassName,
              segmentTone,
              'disabled:cursor-not-allowed disabled:opacity-50',
              hasWrapper && 'w-full',
              hasWrapper && option.disabled && 'pointer-events-none',
              itemClassName,
            )}
          >
            {option.label}
          </button>
        );
        if (!hasWrapper) return button;
        return (
          <span key={option.value} title={option.title} className="flex flex-1">
            {button}
          </span>
        );
      })}
    </div>
  );
};

export default SegmentedControl;
